import React, { useState, useRef, useEffect } from "react";
import { FaWhatsapp, FaTimes } from "react-icons/fa";

const WhatsAppFloatingButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const chatRef = useRef(null);
  const buttonRef = useRef(null);
  const phoneNumber = "254740000444";

  const [buttonPos, setButtonPos] = useState({
    top: window.innerHeight - 100,
    left: window.innerWidth - 80,
  });

  const dragInfo = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    origTop: 0,
    origLeft: 0,
  });

  const clampPosition = (top, left) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const elem = buttonRef.current;
    if (!elem) return { top, left };
    const rect = elem.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const clampedTop = Math.min(Math.max(0, top), vh - height);
    const clampedLeft = Math.min(Math.max(0, left), vw - width);
    return { top: clampedTop, left: clampedLeft };
  };

  const onDragStart = (e) => {
    e.preventDefault();
    dragInfo.current.dragging = true;
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    dragInfo.current.startX = clientX;
    dragInfo.current.startY = clientY;
    dragInfo.current.origTop = buttonPos.top;
    dragInfo.current.origLeft = buttonPos.left;

    document.addEventListener("mousemove", onDragMove);
    document.addEventListener("mouseup", onDragEnd);
    document.addEventListener("touchmove", onDragMove, { passive: false });
    document.addEventListener("touchend", onDragEnd);
  };

  const onDragMove = (e) => {
    if (!dragInfo.current.dragging) return;
    e.preventDefault();
    const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
    const deltaX = clientX - dragInfo.current.startX;
    const deltaY = clientY - dragInfo.current.startY;
    const newTop = dragInfo.current.origTop + deltaY;
    const newLeft = dragInfo.current.origLeft + deltaX;
    if (buttonRef.current) {
      const clamped = clampPosition(newTop, newLeft);
      setButtonPos(clamped);
    }
  };

  const onDragEnd = () => {
    dragInfo.current.dragging = false;
    document.removeEventListener("mousemove", onDragMove);
    document.removeEventListener("mouseup", onDragEnd);
    document.removeEventListener("touchmove", onDragMove);
    document.removeEventListener("touchend", onDragEnd);
  };

  const handleSend = (e) => {
    e.preventDefault();
    const message = e.target.message.value.trim();
    if (!message) return;
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
    e.target.reset();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatRef.current && !chatRef.current.contains(event.target) && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  const [chatHeight, setChatHeight] = useState(300);
  useEffect(() => {
    if (chatRef.current) {
      setChatHeight(chatRef.current.getBoundingClientRect().height);
    }
  }, [isOpen]);

  const chatTop = buttonPos.top - chatHeight - 10;
  const chatLeft = buttonPos.left;

  return (
    <>
      {/* Floating Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open WhatsApp chat"
        className={`
          fixed z-50 flex items-center justify-center
          w-14 h-14 sm:w-16 sm:h-16
          rounded-full
          bg-[rgba(212,175,55,0.15)] backdrop-blur-lg
          border border-[rgba(212,175,55,0.3)]
          text-white hover:bg-[rgba(212,175,55,0.3)]
          hover:scale-110 transition-transform duration-300
          focus:outline-none focus:ring-2 focus:ring-yellow-300
          cursor-move
        `}
        style={{
          top: buttonPos.top,
          left: buttonPos.left,
          position: "fixed",
        }}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
      >
        {isOpen ? <FaTimes size={24} /> : <FaWhatsapp size={24} />}
      </button>

      {/* Chatbox */}
      {isOpen && (
        <div
          ref={chatRef}
          className="
            fixed z-50
            w-[90vw] sm:w-80 max-w-sm
            bg-white/90 dark:bg-black/90 backdrop-blur-md
            border border-black/10 dark:border-white/20
            rounded-xl flex flex-col shadow-lg
            transition-all duration-300
            select-text
          "
          style={{
            top: chatTop > 0 ? chatTop : 10,
            left: chatLeft + 10 > window.innerWidth - 300 ? window.innerWidth - 310 : chatLeft,
          }}
        >
          {/* Header */}
          <div className="flex justify-between items-center bg-yellow-800 bg-opacity-70 px-4 py-3 rounded-t-xl select-none">
            <h4 className="text-base font-semibold font-mono text-white">
              Chat with us
            </h4>
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
              className="text-white hover:text-yellow-400"
            >
              <FaTimes />
            </button>
          </div>

          {/* Message Area */}
          <div className="flex-1 p-4 overflow-y-auto text-black dark:text-white text-sm font-mono max-h-64">
            <p className="mb-2">Hello! 👋 How can we assist you today?</p>
          </div>

          {/* Input Form */}
          <form
            className="border-t border-black/10 dark:border-white/20 px-3 py-3 flex gap-2"
            onSubmit={handleSend}
          >
            <input
              name="message"
              type="text"
              placeholder="Type your message..."
              className="
                flex-grow px-3 py-2
                bg-transparent border border-black/20 dark:border-white/30
                rounded-md text-black dark:text-white placeholder-black/60 dark:placeholder-white/70
                focus:outline-none focus:ring-2 focus:ring-yellow-500
                transition text-sm
              "
              autoComplete="off"
            />
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded-md font-semibold bg-yellow-600 text-white hover:bg-yellow-500 transition-colors duration-300"
            >
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default WhatsAppFloatingButton;
